import * as React from 'react';
import {
  Box, Chip, CircularProgress, Collapse, TextField,
  IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SecurityIcon from '@mui/icons-material/Security';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldIcon from '@mui/icons-material/Shield';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArticleIcon from '@mui/icons-material/Article';
import api from '../services/api';

function flattenObj(obj, prefix = '', out = {}) {
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      flattenObj(v, key, out);
    } else {
      out[key] = Array.isArray(v) ? JSON.stringify(v) : v;
    }
  }
  return out;
}

const INDICATOR_COLORS = {
  ip: 'error',
  compromised_ip: 'error',
  url: 'warning',
  cve: 'error',
  username: 'info',
  port: 'default',
  file: 'warning',
  hash_sha1: 'default',
  path: 'default',
  package: 'info',
};

export default function TabWazuh({ props }) {
  const [context, setContext] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expandedEvent, setExpandedEvent] = React.useState(null);
  const [showRawLog, setShowRawLog] = React.useState(false);
  const [docTab, setDocTab] = React.useState('table');
  const [docSearch, setDocSearch] = React.useState('');

  React.useEffect(() => {
    if (!props.codEmpresa || !props.eventId) return;
    setLoading(true);
    api.get(`dashboard/wazuh-context/${props.codEmpresa}/${props.eventId}`)
      .then(res => setContext(res.data))
      .catch(() => setContext(null))
      .finally(() => setLoading(false));
  }, [props.codEmpresa, props.eventId]);

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  if (!context) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Sem contexto Wazuh disponivel para este alarme.
        </Typography>
      </Box>
    );
  }

  const desc = context.incident_description || {};

  return (
    <Stack spacing={1.5}>
      {/* Incidente + Resumo */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SecurityIcon fontSize="small" color="error" />
          <Typography variant="subtitle2">Incidente</Typography>
          <Chip label={context.incident_label || context.incident_type} size="small" color="error" variant="outlined" />
        </Box>
        {context.summary && (
          <Typography variant="body2" sx={{ mb: 1 }}>{context.summary}</Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
          {desc.host && <DetailItem label="Host" value={desc.host} />}
          {desc.agent_ip && <DetailItem label="Agent IP" value={desc.agent_ip} />}
          {desc.source_ip && <DetailItem label="Source IP" value={desc.source_ip} />}
          {desc.target_user && <DetailItem label="Usuario alvo" value={desc.target_user} />}
          {desc.service && <DetailItem label="Servico" value={desc.service} />}
          {desc.rule_id && <DetailItem label="Rule ID" value={desc.rule_id} />}
          {desc.total_attempts && <DetailItem label="Tentativas" value={desc.total_attempts} />}
          {desc.period && <DetailItem label="Periodo" value={desc.period} />}
          {desc.country && <DetailItem label="Pais" value={desc.country} />}
          {desc.login_success && <DetailItem label="Login com sucesso" value={desc.login_success} />}
        </Box>
      </Paper>

      {/* Timeline */}
      {context.timeline && context.timeline.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TimelineIcon fontSize="small" />
            <Typography variant="subtitle2">Timeline (eventos/hora)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 60 }}>
            {(() => {
              const maxCount = Math.max(...context.timeline.map(t => t.count), 1);
              return context.timeline.map((t, i) => (
                <Box
                  key={i}
                  title={`${new Date(t.timestamp).toLocaleString('pt-BR')}: ${t.count}`}
                  sx={{
                    flex: 1,
                    minWidth: 4,
                    height: `${Math.max((t.count / maxCount) * 100, 2)}%`,
                    backgroundColor: t.count > 0 ? 'error.main' : 'action.disabled',
                    borderRadius: '2px 2px 0 0',
                    opacity: t.count > 0 ? 0.8 : 0.3,
                  }}
                />
              ));
            })()}
          </Box>
        </Paper>
      )}

      {/* Indicadores + MITRE */}
      {(context.indicators?.length > 0 || (context.mitre && (context.mitre.tactics?.length > 0 || context.mitre.techniques?.length > 0))) && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          {context.indicators?.length > 0 && (
            <Box sx={{ mb: context.mitre ? 1.5 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <GpsFixedIcon fontSize="small" />
                <Typography variant="subtitle2">Indicadores ({context.indicators.length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {context.indicators.map((ind, i) => (
                  <Chip
                    key={i}
                    label={`${ind.type}: ${ind.value}${ind.count > 1 ? ` (${ind.count}x)` : ''}`}
                    size="small"
                    color={INDICATOR_COLORS[ind.type] || 'default'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          {context.mitre && (context.mitre.tactics?.length > 0 || context.mitre.techniques?.length > 0) && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShieldIcon fontSize="small" />
                <Typography variant="subtitle2">MITRE ATT&CK</Typography>
              </Box>
              {context.mitre.tactics?.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Taticas</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
                    {context.mitre.tactics.map((t, i) => (
                      <Chip key={i} label={`${t.name} (${t.count})`} size="small" color="warning" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              {context.mitre.techniques?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Tecnicas</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
                    {context.mitre.techniques.map((t, i) => (
                      <Chip key={i} label={`${t.id} (${t.count})`} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Eventos Relacionados */}
      {context.related_events && context.related_events.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>
            Eventos Relacionados ({context.related_events.length})
          </Typography>
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>Hora</TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>Descricao</TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>Lvl</TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>SrcIP</TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem', width: 30 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {context.related_events.map((evt, i) => (
                  <React.Fragment key={i}>
                    <TableRow
                      hover
                      sx={{ cursor: evt.full_source ? 'pointer' : 'default' }}
                      onClick={() => evt.full_source && setExpandedEvent(expandedEvent === i ? null : i)}
                    >
                      <TableCell sx={{ py: 0.3, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 0.3, fontSize: '0.7rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {evt.description}
                      </TableCell>
                      <TableCell sx={{ py: 0.3, fontSize: '0.7rem' }}>{evt.level}</TableCell>
                      <TableCell sx={{ py: 0.3, fontSize: '0.7rem' }}>{evt.srcip || '—'}</TableCell>
                      <TableCell sx={{ py: 0.3 }}>
                        {evt.full_source && (
                          <IconButton size="small" sx={{ p: 0 }}>
                            {expandedEvent === i ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedEvent === i && evt.full_source && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0.5 }}>
                          <Box sx={{ maxHeight: 200, overflow: 'auto', backgroundColor: 'background.default', borderRadius: 1, p: 1 }}>
                            <pre style={{ fontSize: '0.65rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                              {JSON.stringify(evt.full_source, null, 2)}
                            </pre>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Acoes Recomendadas */}
      {context.recommended_actions && context.recommended_actions.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon fontSize="small" color="warning" />
            <Typography variant="subtitle2">Acoes Recomendadas</Typography>
          </Box>
          {context.recommended_actions.map((action, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.3 }}>
              {i + 1}. {action}
            </Typography>
          ))}
        </Paper>
      )}

      {/* Documento Elasticsearch */}
      {context.related_events?.[0]?.full_source && (() => {
        const docSource = context.related_events[0].full_source;
        const search = docSearch.toLowerCase();
        const flat = flattenObj(docSource);
        const filteredEntries = Object.entries(flat).filter(
          ([k, v]) => !search || k.toLowerCase().includes(search) || String(v).toLowerCase().includes(search)
        );
        const jsonStr = JSON.stringify(docSource, null, 2);
        return (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => setShowRawLog(!showRawLog)}
            >
              <ArticleIcon fontSize="small" />
              <Typography variant="subtitle2">Documento</Typography>
              {showRawLog ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>
            <Collapse in={showRawLog}>
              <Box sx={{ mt: 1, p: 1, backgroundColor: grey[900], borderRadius: 1 }}>
                {/* Tabs + Search */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label="Table"
                    size="small"
                    onClick={() => setDocTab('table')}
                    sx={{
                      height: 22, fontSize: '0.7rem', cursor: 'pointer',
                      backgroundColor: docTab === 'table' ? blue[700] : grey[700],
                      color: '#fff',
                    }}
                  />
                  <Chip
                    label="JSON"
                    size="small"
                    onClick={() => setDocTab('json')}
                    sx={{
                      height: 22, fontSize: '0.7rem', cursor: 'pointer',
                      backgroundColor: docTab === 'json' ? blue[700] : grey[700],
                      color: '#fff',
                    }}
                  />
                  <TextField
                    size="small"
                    placeholder="Buscar campo ou valor..."
                    value={docSearch}
                    onChange={e => setDocSearch(e.target.value)}
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': { height: 26, fontSize: '0.75rem', color: grey[100], backgroundColor: grey[800], borderRadius: 1 },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: grey[700] },
                    }}
                  />
                </Box>
                {/* Table view */}
                {docTab === 'table' && (
                  <TableContainer sx={{ maxHeight: 350 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.2, fontWeight: 600, fontSize: '0.65rem', color: grey[400], borderBottom: `1px solid ${grey[700]}` }}>Campo</TableCell>
                          <TableCell sx={{ py: 0.2, fontWeight: 600, fontSize: '0.65rem', color: grey[400], borderBottom: `1px solid ${grey[700]}` }}>Valor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEntries.map(([k, v]) => (
                          <TableRow key={k}>
                            <TableCell sx={{ py: 0.15, fontSize: '0.7rem', color: blue[300], fontFamily: 'monospace', borderBottom: `1px solid ${grey[800]}`, whiteSpace: 'nowrap' }}>{k}</TableCell>
                            <TableCell sx={{ py: 0.15, fontSize: '0.7rem', color: grey[100], fontFamily: 'monospace', borderBottom: `1px solid ${grey[800]}`, wordBreak: 'break-all' }}>{String(v)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {/* JSON view */}
                {docTab === 'json' && (
                  <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                    <pre style={{ margin: 0, color: grey[100], fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {search
                        ? jsonStr.split('\n').map((line, li) => {
                            const lower = line.toLowerCase();
                            if (!lower.includes(search)) return <span key={li} style={{ opacity: 0.3 }}>{line}{'\n'}</span>;
                            return <span key={li} style={{ backgroundColor: 'rgba(255,213,79,0.2)' }}>{line}{'\n'}</span>;
                          })
                        : jsonStr}
                    </pre>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>
        );
      })()}
    </Stack>
  );
}

function DetailItem({ label, value }) {
  return (
    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
      <b>{label}:</b> {value}
    </Typography>
  );
}
